import { Globe2 } from "lucide-react";
import { translations } from "../../i18n";

type Translation = (typeof translations)["de"];
type Lang = "de" | "en";

function LanguageSwitcher({
  lang,
  onChange,
  t,
  theme = "dark",
}: {
  lang: Lang;
  onChange: (lang: Lang) => void;
  t: Translation["nav"];
  theme?: "dark" | "light";
}) {
  const isDark = theme === "dark";

  // Theme-based styles
  const pillBg = isDark ? "bg-slate-900 border-slate-700" : "bg-slate-100 border-slate-200";
  const inactiveText = isDark ? "text-slate-300 hover:text-white" : "text-slate-500 hover:text-slate-900";
  const mobileBg = isDark ? "bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50";
  const dropdownBg = isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200 shadow-lg";
  const dropdownItemActive = isDark ? "text-white bg-slate-800" : "text-slate-900 bg-orange-50";
  const dropdownItemInactive = isDark ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-50";

  return (
    <div className="relative" role="group" aria-label={t.languageLabel}>
      {/* Desktop: pill switch */}
      <div className="hidden md:flex items-center">
        <div className={`inline-flex items-center rounded-full border p-1 ${pillBg}`}>
          <button
            onClick={() => onChange("de")}
            className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
              lang === "de"
                ? "bg-orange-500 text-white shadow-sm"
                : inactiveText
            }`}
          >
            {t.de}
          </button>
          <button
            onClick={() => onChange("en")}
            className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
              lang === "en"
                ? "bg-orange-500 text-white shadow-sm"
                : inactiveText
            }`}
          >
            {t.en}
          </button>
        </div>
      </div>

      {/* Mobile: globe icon with dropdown */}
      <div className="flex md:hidden items-center">
        <details className="relative">
          <summary className={`list-none flex items-center justify-center w-9 h-9 rounded-full border cursor-pointer ${mobileBg}`}>
            <Globe2 className="w-4 h-4" />
          </summary>
          <div className={`absolute right-0 mt-2 w-32 rounded-xl border py-1 z-50 ${dropdownBg}`}>
            <button
              type="button"
              onClick={() => {
                onChange("de");
              }}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-xs ${
                lang === "de" ? dropdownItemActive : dropdownItemInactive
              }`}
            >
              <span>🇩🇪</span>
              <span>{t.de}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                onChange("en");
              }}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-xs ${
                lang === "en" ? dropdownItemActive : dropdownItemInactive
              }`}
            >
              <span>🇬🇧</span>
              <span>{t.en}</span>
            </button>
          </div>
        </details>
      </div>
    </div>
  );
}

export default LanguageSwitcher;